import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BarChartIcon from '@mui/icons-material/BarChart';
import ForumIcon from '@mui/icons-material/Forum';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { COLORS } from '../theme/colors';
import { GlowCard, SectionHeader, SectionTag } from '../components/ui/SharedUI';
import PageLayout from '../components/layout/PageLayout';
import type { Capability, HeroContent, MethodologyContent, Step } from '../types/content';

type StepCardProps = {
  step: Step;
};

function StepCard({ step }: StepCardProps): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <GlowCard sx={{ height: '100%' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '9px',
          background: isDark ? COLORS.dark[600] : COLORS.light[300],
          border: `1px solid ${isDark ? COLORS.dark[400] : COLORS.light[400]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.78rem',
            color: 'text.secondary',
            lineHeight: 1,
          }}
        >
          {step.id}
        </Typography>
      </Box>

      <Typography
        variant="h6"
        sx={{
          mb: 1.5,
          fontSize: '1rem',
          fontWeight: 700,
          lineHeight: 1.35,
        }}
      >
        {step.title}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          lineHeight: 1.75,
          fontSize: '0.92rem',
        }}
      >
        {step.description}
      </Typography>
    </GlowCard>
  );
}

type CapabilityTagIconProps = {
  id: string;
};

function CapabilityTagIcon({ id }: CapabilityTagIconProps): JSX.Element {
  const iconSx = {
    fontSize: 12,
    color: '#d8b4fe',
  };

  if (id === 'welcome') return <ForumIcon sx={iconSx} />;
  if (id === 'horario') return <AccessTimeIcon sx={iconSx} />;
  if (id === 'seguimiento') return <BarChartIcon sx={iconSx} />;
  if (id === 'calificacion') return <StarBorderIcon sx={iconSx} />;

  return <AutoAwesomeIcon sx={iconSx} />;
}

type MiniTagProps = {
  id: string;
  label: string;
};

function MiniTag({ id, label }: MiniTagProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.65,
        px: 1.15,
        py: 0.45,
        borderRadius: '999px',
        background: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: COLORS.gray[300],
        fontSize: '0.72rem',
        fontWeight: 600,
        lineHeight: 1,
        width: 'fit-content',
        mb: 2.8,
      }}
    >
      <CapabilityTagIcon id={id} />
      {label}
    </Box>
  );
}

type ChatBubbleProps = {
  children: ReactNode;
  variant?: 'question' | 'answer' | 'dark';
  align?: 'left' | 'right';
};

function ChatBubble({ children, variant = 'dark', align = 'left' }: ChatBubbleProps): JSX.Element {
  const isAnswer = variant === 'answer';
  const isQuestion = variant === 'question';

  return (
    <Box
      sx={{
        alignSelf: align === 'right' ? 'flex-end' : 'flex-start',
        maxWidth: isQuestion ? '72%' : '78%',
        px: 1.6,
        py: 1.25,
        borderRadius: '5px',
        background: isAnswer
          ? 'linear-gradient(135deg, rgba(88,36,121,0.98), rgba(66,35,103,0.98))'
          : isQuestion
            ? 'transparent'
            : 'linear-gradient(135deg, rgba(59,45,89,0.98), rgba(49,39,78,0.98))',
        border: isAnswer ? '1px solid rgba(216,180,254,0.12)' : '1px solid transparent',
      }}
    >
      <Typography
        sx={{
          color: isQuestion ? COLORS.gray[200] : '#f5f3ff',
          fontSize: { xs: '0.62rem', md: '0.66rem' },
          lineHeight: 1.45,
          textAlign: isQuestion ? 'right' : 'left',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

type PreviewPanelProps = {
  children: ReactNode;
};

function PreviewPanel({ children }: PreviewPanelProps): JSX.Element {
  return (
    <Box
      sx={{
        width: { xs: '100%', md: 270 },
        minHeight: { xs: 132, md: 150 },
        borderRadius: '6px',
        background: 'rgba(255,255,255,0.13)',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
        p: 2.2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

function WelcomePreview(): JSX.Element {
  return (
    <PreviewPanel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
        <ChatBubble variant="answer">
          ¡Hola! 👋 Soy Nova, el asistente virtual. ¿En qué te puedo ayudar hoy con tu negocio?
        </ChatBubble>

        <ChatBubble variant="question" align="right">
          Quiero info sobre planes.
        </ChatBubble>
      </Box>
    </PreviewPanel>
  );
}

function AfterHoursPreview(): JSX.Element {
  return (
    <PreviewPanel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
        <ChatBubble variant="question" align="right">
          ¿Están abiertos?
        </ChatBubble>

        <ChatBubble variant="answer">
          Actualmente nuestra oficina está cerrada, pero puedo tomar tus datos y un asesor te contactará a primera hora
          mañana. 🌙
        </ChatBubble>
      </Box>
    </PreviewPanel>
  );
}

function FollowUpPreview(): JSX.Element {
  const bars = [24, 36, 28, 42, 58, 44, 34, 62, 50, 71, 46, 60, 38, 64, 52, 47];

  return (
    <PreviewPanel>
      <Box
        sx={{
          width: '100%',
          height: 132,
          borderRadius: '6px',
          background: 'linear-gradient(180deg, rgba(29,25,44,0.8), rgba(19,17,31,0.92))',
          border: '1px solid rgba(255,255,255,0.04)',
          overflow: 'hidden',
          position: 'relative',
          opacity: 0.72,
        }}
      >
        <Box
          sx={{
            height: 22,
            display: 'flex',
            alignItems: 'center',
            px: 1.2,
            gap: 0.7,
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.24)' }} />
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.18)' }} />
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
        </Box>

        <Box sx={{ px: 1.5, pt: 1.2 }}>
          <Box sx={{ width: '48%', height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.13)', mb: 1 }} />
          <Box sx={{ width: '82%', height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.08)', mb: 0.8 }} />
          <Box sx={{ width: '68%', height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.07)', mb: 1.6 }} />

          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.45, height: 52 }}>
            {bars.map((height, index) => (
              <Box
                key={index}
                sx={{
                  width: 6,
                  height,
                  borderRadius: '2px 2px 0 0',
                  background:
                    index > 9
                      ? 'linear-gradient(180deg, rgba(168,85,247,0.36), rgba(168,85,247,0.10))'
                      : 'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05))',
                }}
              />
            ))}
          </Box>

          <Box sx={{ width: '34%', height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.08)', mt: 1.4 }} />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, transparent 0%, rgba(20,17,33,0.34) 100%)',
          }}
        />
      </Box>
    </PreviewPanel>
  );
}

function RatingPreview(): JSX.Element {
  return (
    <PreviewPanel>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            color: COLORS.gray[200],
            fontSize: { xs: '0.72rem', md: '0.76rem' },
            lineHeight: 1.5,
            mb: 2.1,
          }}
        >
          ¿Cómo calificarías la atención de nuestro agente?
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.9 }}>
          {[1, 2, 3, 4, 5].map((item) => (
            <StarBorderIcon
              key={item}
              sx={{
                fontSize: 22,
                color: 'rgba(216,180,254,0.7)',
              }}
            />
          ))}
        </Box>
      </Box>
    </PreviewPanel>
  );
}

type CapabilityPreviewProps = {
  cap: Capability;
};

function CapabilityPreview({ cap }: CapabilityPreviewProps): JSX.Element {
  if (cap.id === 'welcome') return <WelcomePreview />;
  if (cap.id === 'horario') return <AfterHoursPreview />;
  if (cap.id === 'seguimiento') return <FollowUpPreview />;
  if (cap.id === 'calificacion') return <RatingPreview />;

  return (
    <PreviewPanel>
      <Typography sx={{ color: COLORS.gray[300], fontSize: '0.76rem', lineHeight: 1.5 }}>{cap.preview}</Typography>
    </PreviewPanel>
  );
}

type CapabilityCardProps = {
  cap: Capability;
};

function CapabilityCard({ cap }: CapabilityCardProps): JSX.Element {
  return (
    <Box
      sx={{
        minHeight: { xs: 360, md: 286 },
        height: '100%',
        borderRadius: '22px',
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(145deg, rgba(28,25,43,0.96) 0%, rgba(20,18,34,0.98) 54%, rgba(28,24,45,0.96) 100%)',
        border: '1px solid rgba(255,255,255,0.055)',
        boxShadow: '0 22px 70px rgba(0,0,0,0.24)',
        p: { xs: 3, md: 3.8 },
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.28s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 86% 20%, rgba(168,85,247,0.10) 0%, transparent 36%), radial-gradient(circle at 8% 95%, rgba(147,51,234,0.08) 0%, transparent 42%)',
          opacity: 0.9,
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: 'rgba(168,85,247,0.22)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.32), 0 0 42px rgba(147,51,234,0.10)',
        },
      }}
    >
      <Grid
        container
        spacing={{ xs: 3, md: 4 }}
        alignItems="center"
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Grid item xs={12} md={5}>
          <MiniTag id={cap.id} label={cap.tag} />

          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontSize: { xs: '1.2rem', md: '1.35rem' },
              lineHeight: 1.32,
              fontWeight: 700,
              mb: 2.2,
              maxWidth: 280,
            }}
          >
            {cap.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[400],
              fontSize: { xs: '0.9rem', md: '0.95rem' },
              lineHeight: 1.65,
              maxWidth: 280,
            }}
          >
            {cap.description}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          <CapabilityPreview cap={cap} />
        </Grid>
      </Grid>
    </Box>
  );
}

export default function ComoFuncionaPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const hero = t('comoFunciona.hero', { returnObjects: true }) as Required<HeroContent>;
  const metod = t('comoFunciona.metodologia', { returnObjects: true }) as MethodologyContent;
  const steps = t('comoFunciona.steps', { returnObjects: true }) as Step[];
  const capabilitiesHeader = t('comoFunciona.capacidades', { returnObjects: true }) as MethodologyContent;
  const caps = t('comoFunciona.caps', { returnObjects: true }) as Capability[];

  return (
    <PageLayout>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            ? `
              radial-gradient(circle at 50% 6%, rgba(196,181,253,0.10) 0%, rgba(168,85,247,0.12) 18%, transparent 38%),
              radial-gradient(circle at 12% 28%, rgba(168,85,247,0.10) 0%, transparent 34%),
              radial-gradient(circle at 88% 24%, rgba(216,180,254,0.10) 0%, transparent 30%),
              radial-gradient(circle at 50% 62%, rgba(168,85,247,0.08) 0%, transparent 34%),
              radial-gradient(circle at 50% 92%, rgba(216,180,254,0.08) 0%, transparent 30%),
              linear-gradient(180deg, ${COLORS.dark[900]} 0%, #0e0a1f 45%, ${COLORS.dark[900]} 100%)
            `
            : `
              radial-gradient(circle at 50% 8%, rgba(168,85,247,0.16) 0%, rgba(216,180,254,0.12) 18%, transparent 38%),
              radial-gradient(circle at 16% 26%, rgba(168,85,247,0.10) 0%, transparent 32%),
              radial-gradient(circle at 84% 26%, rgba(216,180,254,0.10) 0%, transparent 30%),
              radial-gradient(circle at 50% 70%, rgba(168,85,247,0.08) 0%, transparent 32%),
              linear-gradient(180deg, ${COLORS.light[100]} 0%, #f7f2ff 52%, ${COLORS.gray[100]} 100%)
            `,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: -180, md: -260 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 620, md: 1050 },
            height: { xs: 620, md: 1050 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(216,180,254,0.14) 0%, rgba(168,85,247,0.10) 30%, transparent 68%)',
            filter: 'blur(70px)',
            opacity: isDark ? 0.72 : 0.42,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: '34%', md: '38%' },
            left: { xs: '-180px', md: '-100px' },
            width: { xs: 360, md: 520 },
            height: { xs: 360, md: 520 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.12) 0%, rgba(216,180,254,0.06) 34%, transparent 74%)',
            filter: 'blur(64px)',
            opacity: isDark ? 0.56 : 0.3,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: '56%', md: '60%' },
            right: { xs: '-180px', md: '-110px' },
            width: { xs: 360, md: 560 },
            height: { xs: 360, md: 560 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(216,180,254,0.12) 0%, rgba(168,85,247,0.06) 36%, transparent 76%)',
            filter: 'blur(68px)',
            opacity: isDark ? 0.5 : 0.26,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Hero */}
          <Box
            sx={{
              minHeight: '55vh',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              py: { xs: 10, md: 6 },
              background: 'transparent',
            }}
          >
            <Container maxWidth="lg">
              <Grid container alignItems="center" spacing={6}>
                <Grid item xs={12} md={6}>
                  <SectionTag label={hero.tag} icon="⚙️" />

                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: {
                        xs: '2rem',
                        sm: '2.45rem',
                        md: '3.15rem',
                        lg: '3.45rem',
                      },
                      lineHeight: {
                        xs: 1.16,
                        md: 1.12,
                      },
                      fontWeight: 700,
                      mt: 1,
                      mb: 2,
                      color: 'text.primary',
                    }}
                  >
                    {hero.title1}{' '}
                    <Box
                      component="span"
                      sx={{
                        background: COLORS.gradients.brand,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {hero.highlight}
                    </Box>
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      maxWidth: 460,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      lineHeight: 1.7,
                    }}
                  >
                    {hero.subtext}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      height: 280,
                      borderRadius: '20px',
                      background: isDark
                        ? 'radial-gradient(ellipse at center, rgba(147,51,234,0.28) 0%, rgba(17,11,35,0.96) 72%)'
                        : 'radial-gradient(ellipse at center, rgba(196,181,253,0.55) 0%, rgba(247,242,255,0.92) 72%)',
                      border: `1px solid ${COLORS.purple[500]}44`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '4rem',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                    }}
                  >
                    🧠
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Methodology */}
          <Box
            sx={{
              py: { xs: 8, md: 12 },
              background: 'transparent',
            }}
          >
            <Container maxWidth="lg">
              <SectionHeader title={metod.title} subtitle={metod.subtext} dark={isDark} />

              <Grid container spacing={3}>
                {steps.map((step) => (
                  <Grid item xs={12} sm={6} md={3} key={step.id}>
                    <StepCard step={step} />
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* Capabilities */}
          <Box
            sx={{
              py: { xs: 8, md: 12 },
              background: 'transparent',
            }}
          >
            <Container maxWidth="xl">
              <SectionHeader title={capabilitiesHeader.title} subtitle={capabilitiesHeader.subtext} dark={isDark} />

              <Grid container spacing={{ xs: 2.8, md: 4 }}>
                {caps.map((cap) => (
                  <Grid item xs={12} md={6} key={cap.id}>
                    <CapabilityCard cap={cap} />
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
}